import React from 'react'
import { Link } from 'react-router-dom'

interface AppShellProps {
  /**
   * Optional left-side back button or breadcrumb.
   */
  leadingNav?: React.ReactNode
  /**
   * Optional right-side action (e.g., close, help).
   */
  trailingNav?: React.ReactNode
  /**
   * Render-prop body. Receives a `compact` flag the page can use to make
   * decisions about whether to render a hero column.
   */
  children: React.ReactNode
  /**
   * If false, the centered DUPR logo in the nav becomes plain text (no Link).
   * Use this when the consumer is rendered inside an iframe partner integration
   * where navigation away would break the host.
   */
  logoLinksToDashboard?: boolean
  /**
   * Optional className applied to the outer page container (rare — most
   * surfaces rely on the default canvas background).
   */
  className?: string
}

/**
 * Shared canvas + sticky nav for the SSO login, product picker, checkout, and
 * Reset product surfaces. Theme tokens drive every color/typography choice so
 * the design picker can swap variants without touching this component.
 *
 * Layout:
 *   ┌──────────────────────────────────────────┐
 *   │  [leading]            DUPR     [trailing]│  sticky h-16
 *   ├──────────────────────────────────────────┤
 *   │                                          │
 *   │              children                    │
 *   │                                          │
 *   └──────────────────────────────────────────┘
 */
export const AppShell: React.FC<AppShellProps> = ({
  leadingNav,
  trailingNav,
  children,
  logoLinksToDashboard = true,
  className = '',
}) => {
  // Both logo variants render at every viewport. CSS visibility is driven
  // by --logo-light-display / --logo-dark-display, which the active theme
  // sets (and which "current" toggles via a media query — see themes.css).
  const logo = (
    <>
      <img
        src="/img/dupr_light.svg"
        alt="DUPR"
        className="h-6 md:h-8 transition-opacity hover:opacity-70"
        style={{ display: 'var(--logo-light-display)' }}
      />
      <img
        src="/img/dupr_dark.svg"
        alt="DUPR"
        className="h-6 md:h-8 transition-opacity hover:opacity-70"
        style={{ display: 'var(--logo-dark-display)' }}
      />
    </>
  )

  return (
    <div
      className={[
        'min-h-screen w-full',
        'bg-[color:var(--canvas)]',
        'text-[color:var(--ink)]',
        'font-[family:var(--body-font)]',
        className,
      ].join(' ')}
    >
      <nav
        className="
          relative h-16
          flex items-center
          px-4 md:px-8
          border-b border-[color:var(--nav-border)]
        "
        style={{
          background: 'var(--nav-bg)',
          color: 'var(--nav-fg)',
        }}
      >
        <div className="flex-1 flex items-center min-w-0">{leadingNav}</div>

        <div className="absolute left-1/2 -translate-x-1/2 flex items-center">
          {logoLinksToDashboard ? (
            <Link to="/dashboard" aria-label="Go to DUPR dashboard">
              {logo}
            </Link>
          ) : (
            logo
          )}
        </div>

        <div className="flex-1 flex items-center justify-end min-w-0">{trailingNav}</div>
      </nav>

      {children}
    </div>
  )
}

/**
 * Centers a max-width card within the AppShell. Used by every login / picker /
 * status surface that should feel constrained — mobile-friendly on narrow
 * iframes, modest on desktop.
 *
 * Children are placed inside an `AppCard` with the standard padding. Use
 * `bare` to opt out of the AppCard wrapper if you need to render
 * full-bleed content (e.g., the success component which has its own padding).
 */
interface CenteredCardProps {
  children: React.ReactNode
  className?: string
  bare?: boolean
  /** max-w-md (default) or max-w-lg for slightly wider surfaces. */
  width?: 'md' | 'lg'
}

export const CenteredCard: React.FC<CenteredCardProps> = ({
  children,
  className = '',
  bare = false,
  width = 'md',
}) => {
  const widthClass = width === 'lg' ? 'max-w-lg' : 'max-w-md'

  return (
    <div className="flex flex-col items-center justify-center w-full px-4 py-10 md:py-16 min-h-[calc(100vh-4rem)]">
      {bare ? (
        <div className={`w-full ${widthClass} ${className}`}>{children}</div>
      ) : (
        <AppCardLite className={`w-full ${widthClass} p-6 md:p-10 ${className}`}>
          {children}
        </AppCardLite>
      )}
    </div>
  )
}

/**
 * Inline lightweight card to avoid a circular dep on AppCard.tsx — same
 * visual treatment but defined locally.
 */
function AppCardLite({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`bg-[color:var(--surface)] rounded-[var(--radius-card)] border border-[color:var(--border)] ${className}`}
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {children}
    </div>
  )
}

interface BackButtonProps {
  onClick?: () => void
  label?: string
}

export const BackButton: React.FC<BackButtonProps> = ({ onClick, label = 'Back' }) => (
  <button
    type="button"
    onClick={onClick}
    className="
      inline-flex items-center gap-2
      text-sm font-medium
      text-[color:inherit]
      hover:opacity-70 transition-opacity
      px-2 py-1 -ml-2
      focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent)]
      rounded
    "
    aria-label={label}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    <span className="hidden sm:inline">{label}</span>
  </button>
)

export default AppShell
